import {
  Ban,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CirclePause,
  Hourglass,
  Laptop,
  Lock,
  Moon,
  Server,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  type LucideIcon,
} from 'lucide-react';
import type { PlatformMembershipStatus, TenantStatus } from '@ticarishift/api-client/platform';
import { hasCopy, t, translate } from '@ticarishift/i18n';
import { cn, familyClasses, type StatusFamily } from '@ticarishift/ui';
import type { BackofficeEnvironment } from '../lib/runtime-config.ts';

type BadgeLook = { readonly family: StatusFamily; readonly icon: LucideIcon; readonly label: string };

/**
 * Platform rozetleri: renk ikon ve metinle birlikte gelir (08 §2 İ-6, E-02). Devam durumu sözlüğü (08 §5.7) mesai
 * durumları içindir; işletme, hesap, iş ve sağlık durumları panelin rozetleri gibi aynı aile renkleriyle çizilir.
 */
export function Badge({ look, size = 'md', className }: { look: BadgeLook; size?: 'sm' | 'md'; className?: string }) {
  const Icon = look.icon;
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-sm font-medium whitespace-nowrap',
        size === 'sm' ? 'h-5 px-1.5 text-caption' : 'h-6 px-2 text-body-sm',
        familyClasses[look.family].soft,
        className,
      )}
    >
      <Icon aria-hidden size={size === 'sm' ? 12 : 14} strokeWidth={1.75} className="shrink-0" />
      <span className="truncate">{look.label}</span>
    </span>
  );
}

const TENANT: Readonly<Record<TenantStatus, { family: StatusFamily; icon: LucideIcon }>> = {
  Trial: { family: 'info', icon: Hourglass },
  Active: { family: 'ok', icon: CircleCheck },
  PastDue: { family: 'attention', icon: CircleAlert },
  ReadOnly: { family: 'attention', icon: Lock },
  Suspended: { family: 'attention', icon: CirclePause },
  Closed: { family: 'neutral', icon: Ban },
};

export const tenantStatusLabel = (status: TenantStatus) => translate(`backoffice.tenants.status.${status}`);

/** İşletmenin durumu (S41): Deneme, Açık, Ödeme gecikti, Salt okunur, Askıda, Kapalı. */
export function TenantStatusBadge({ status, size }: { status: TenantStatus; size?: 'sm' | 'md' }) {
  return <Badge look={{ ...TENANT[status], label: tenantStatusLabel(status) }} size={size} />;
}

/**
 * İşletme uykuda (S72, ADR-016): Ücretsiz planda uzun süredir kullanılmıyor; uyku durum değildir, durum rozetinin yanında gösterilir.
 */
export function SleepBadge({ size }: { size?: 'sm' | 'md' }) {
  return <Badge look={{ family: 'neutral', icon: Moon, label: t('backoffice.tenants.sleep.badge') }} size={size} />;
}

/** Platform kullanıcısının üyelik durumu. */
export function PlatformUserStatusBadge({ status }: { status: PlatformMembershipStatus }) {
  const look: BadgeLook =
    status === 'Active'
      ? { family: 'ok', icon: CircleCheck, label: t('backoffice.users.status.Active') }
      : status === 'Suspended'
        ? { family: 'attention', icon: CirclePause, label: t('backoffice.users.status.Suspended') }
        : { family: 'neutral', icon: Ban, label: t('backoffice.users.status.Disabled') };
  return <Badge look={look} size="sm" />;
}

/** İki adımlı doğrulama: kurulu ya da ilk oturumu bekliyor. */
export function MfaBadge({ enabled }: { enabled: boolean }) {
  const look: BadgeLook = enabled
    ? { family: 'ok', icon: ShieldCheck, label: t('backoffice.users.mfa.on') }
    : { family: 'attention', icon: ShieldOff, label: t('backoffice.users.mfa.off') };
  return <Badge look={look} size="sm" />;
}

/** Sağlık bileşeni (`/health/ready`): Healthy, Degraded, Unhealthy; bilinmeyen değer olduğu gibi. */
export function HealthBadge({ status }: { status: string }) {
  const key = `backoffice.overview.health.states.${status}`;
  const label = hasCopy(key) ? translate(key) : status;
  const look: BadgeLook =
    status === 'Healthy'
      ? { family: 'ok', icon: CircleCheck, label }
      : status === 'Degraded'
        ? { family: 'attention', icon: CircleAlert, label }
        : { family: 'danger', icon: ShieldAlert, label };
  return <Badge look={look} size="sm" />;
}

/** Zamanlanmış işin son sonucu. */
export function JobStatusBadge({ status }: { status: string | null }) {
  const normalized = status?.toLocaleLowerCase('en-US') ?? null;
  let look: BadgeLook;
  if (normalized === null) {
    look = { family: 'neutral', icon: CircleDashed, label: t('backoffice.overview.jobs.status.none') };
  } else if (['succeeded', 'success', 'ok', 'completed'].includes(normalized)) {
    look = { family: 'ok', icon: CircleCheck, label: t('backoffice.overview.jobs.status.succeeded') };
  } else if (['failed', 'failure', 'error'].includes(normalized)) {
    look = { family: 'danger', icon: CircleAlert, label: t('backoffice.overview.jobs.status.failed') };
  } else if (['running', 'started'].includes(normalized)) {
    look = { family: 'info', icon: Hourglass, label: t('backoffice.overview.jobs.status.running') };
  } else {
    look = { family: 'neutral', icon: CircleDashed, label: t('backoffice.overview.jobs.status.other', { status: status ?? '' }) };
  }

  return <Badge look={look} size="sm" />;
}

/** Ortam rozeti (Yerel, Prod): prod her ekranda uyarı rengindedir. */
export function EnvironmentBadge({ environment }: { environment: BackofficeEnvironment }) {
  const name = environment === 'production' ? t('backoffice.environment.production') : t('backoffice.environment.local');
  const look: BadgeLook =
    environment === 'production' ? { family: 'danger', icon: Server, label: name } : { family: 'info', icon: Laptop, label: name };
  return (
    <span className="inline-flex" data-environment={environment}>
      <span className="sr-only">{t('backoffice.environment.label', { name })}</span>
      <span aria-hidden className="inline-flex">
        <Badge look={look} />
      </span>
    </span>
  );
}

/** "Platform" rozeti: bu uygulamanın müşteri paneli olmadığını her ekranda söyler. */
export function PlatformBadge() {
  return <Badge look={{ family: 'extra', icon: ShieldCheck, label: t('backoffice.badge') }} />;
}
